import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { inr } from "../lib/api";
import { FadeUp } from "../components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { LogOut, Package } from "lucide-react";

const STATUS_STEPS = ["pending", "confirmed", "shipped", "delivered"];

export default function Account() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (ready && !user) navigate("/auth?redirect=/account");
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [ready, user]);

  if (!user) return <div className="pt-40 pb-40 text-center font-serif text-3xl">Loading…</div>;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="pt-20" data-testid="account-page">
      <div className="px-5 md:px-10 pt-16 pb-10 max-w-[1200px] mx-auto flex items-end justify-between flex-wrap gap-4">
        <div>
          <FadeUp><p className="overline text-gold-deep mb-3">My Account</p></FadeUp>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight">{user.name}</h1>
          <p className="text-ink-soft mt-2">{user.email}</p>
        </div>
        <div className="flex items-center gap-4">
          {user.role === "admin" && (
            <button onClick={() => navigate("/admin")} className="btn-outline px-6 py-3 overline" data-testid="goto-admin">Admin Panel</button>
          )}
          <button onClick={doLogout} className="flex items-center gap-2 overline link-underline" data-testid="logout-btn"><LogOut size={14} /> Sign out</button>
        </div>
      </div>

      <div className="px-5 md:px-10 max-w-[1200px] mx-auto pb-28">
        <Tabs defaultValue="orders">
          <TabsList className="bg-transparent border-b border-line rounded-none w-full justify-start gap-8 p-0 h-auto mb-8">
            <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-ink data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 overline" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="profile" className="rounded-none border-b-2 border-transparent data-[state=active]:border-ink data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 overline" data-testid="tab-profile">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            {orders.length === 0 ? (
              <div className="py-24 text-center">
                <Package size={32} className="mx-auto text-ink-soft mb-4" />
                <p className="font-serif text-3xl mb-3">No orders yet</p>
                <button onClick={() => navigate("/shop")} className="overline link-underline text-gold-deep">Start shopping</button>
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((o) => (
                  <div key={o.order_id} className="border border-line p-6" data-testid={`order-${o.order_id}`}>
                    <div className="flex justify-between items-start flex-wrap gap-3 mb-5">
                      <div>
                        <p className="mono text-xs text-ink-soft">#{o.order_id.slice(0, 8).toUpperCase()}</p>
                        <p className="text-sm text-ink-soft mt-1">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
                      </div>
                      <div className="text-right">
                        <span className="overline px-3 py-1 bg-ink text-ivory capitalize">{o.status}</span>
                        <p className="font-serif text-2xl mt-2">{inr(o.total)}</p>
                      </div>
                    </div>
                    {/* tracking */}
                    <div className="flex items-center mb-5">
                      {STATUS_STEPS.map((s, i) => {
                        const idx = STATUS_STEPS.indexOf(o.status);
                        const done = i <= idx;
                        return (
                          <div key={s} className="flex items-center flex-1 last:flex-none">
                            <div className={`h-2.5 w-2.5 rounded-full ${done ? "bg-gold" : "bg-line"}`} />
                            {i < STATUS_STEPS.length - 1 && <div className={`h-px flex-1 ${i < idx ? "bg-gold" : "bg-line"}`} />}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      {o.items.map((it, idx) => (
                        <img key={idx} src={it.image} alt={it.name} className="h-24 w-20 object-cover bg-[#efece6]" title={it.name} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile">
            <div className="max-w-md space-y-5">
              <div className="border border-line p-6">
                <p className="overline text-ink-soft mb-1">Name</p>
                <p className="font-serif text-2xl">{user.name}</p>
              </div>
              <div className="border border-line p-6">
                <p className="overline text-ink-soft mb-1">Email</p>
                <p className="font-serif text-2xl">{user.email}</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
